
import type { AccountType } from "@/modules/safecheck/auth";
import { isSuperAdminUser } from "@/modules/safecheck/auth";

const superAdminSystemSection: NavSection = {
  id: "system",
  titleKey: "systemSection",
  items: [
    {
      id: "backup",
      iconName: "settings",
      children: [
        { id: "createBackup", href: "/dashboard/backup/create" },
        { id: "restoreBackup", href: "/dashboard/backup/restore" },
      ],
    },
  ],
};

export const mainSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "main",
      items: [
        { id: "dashboard", iconName: "home", href: "/dashboard/admin" },
      ],
    },
    {
      id: "gestion",
      titleKey: "management",
      items: [
        {
          id: "users",
          iconName: "users",
          children: [
            { id: "allAccounts", href: "/dashboard/users" },
            { id: "addUser", href: "/dashboard/users/create" },
          ],
        },
        {
          id: "providers",
          iconName: "building",
          children: [
            { id: "allProviders", href: "/dashboard/providers" },
            { id: "addProvider", href: "/dashboard/providers/create" },
          ],
        },
        {
          id: "employees",
          iconName: "user-cog",
          children: [
            { id: "allEmployees", href: "/dashboard/employees" },
            { id: "addEmployee", href: "/dashboard/employees/create" },
            { id: "beneficiaries", href: "/dashboard/beneficiaries" },
            { id: "branches", href: "/dashboard/branches" },
          ],
        },
        {
          id: "acts",
          iconName: "stethoscope",
          children: [
            { id: "hospitalActs", href: "/dashboard/acts/by-hospital" },
            { id: "consultActs", href: "/dashboard/acts/standard-codes" },
            { id: "consultDiagnostics", href: "/dashboard/acts/standard-diagnostics" },
          ],
        },
        { id: "pharmacyCatalog", iconName: "package", href: "/dashboard/pharmacy/catalog" },
      ],
    },
    {
      id: "structure",
      titleKey: "structure",
      items: [
        { id: "admissions", iconName: "plus-square", href: "/dashboard/admissions" },
        {
          id: "invoices",
          iconName: "file-text",
          children: [
            { id: "allInvoices", href: "/dashboard/invoices" },
            { id: "approvedInvoices", href: "/dashboard/invoices/approved" },
            { id: "rejectedInvoices", href: "/dashboard/invoices/rejected" },
            { id: "auditedInvoices", href: "/dashboard/invoices/audited" },
            { id: "submitInvoice", href: "/dashboard/invoices/submit" },
            { id: "companyBilling", href: "/dashboard/company-billing" },
            { id: "createCompanyBilling", href: "/dashboard/company-billing/create" },
          ],
        },
        {
          id: "statistics",
          iconName: "bar-chart",
          children: [
            { id: "globalStatistics", href: "/dashboard/statistics" },
          ],
        },
      ],
    },
    {
      id: "forfaits",
      titleKey: "packagesSection",
      items: [
        {
          id: "packages",
          iconName: "package",
          children: [
            { id: "allPackages", href: "/dashboard/packages" },
            { id: "createPackage", href: "/dashboard/packages/create" },
          ],
        },
        {
          id: "conventions",
          iconName: "file-text",
          children: [
            { id: "allConventions", href: "/dashboard/conventions" },
            { id: "createConvention", href: "/dashboard/conventions/create" },
            { id: "budgetConsumption", href: "/dashboard/budget-consumption" },
          ],
        },
        { id: "requests", iconName: "clipboard", href: "/dashboard/requests" },
      ],
    },
    {
      id: "communication",
      titleKey: "communication",
      items: [{ id: "messaging", iconName: "message-circle", href: "/dashboard/messaging" }],
    },
    {
      id: "configuration",
      titleKey: "configuration",
      items: [
        { id: "exchangeRates", iconName: "settings", href: "/dashboard/exchange-rates" },
        {
          id: "settings",
          iconName: "user-cog",
          children: [{ id: "userProfile", href: "/dashboard/settings/user" }],
        },
      ],
    },
  ],
};

/** Navigation compte auditeur */
export const auditorSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "auditor-main",
      items: [
        {
          id: "auditorDashboard",
          iconName: "home",
          href: "/dashboard/auditor",
        },
      ],
    },
    {
      id: "audit",
      titleKey: "auditSection",
      items: [
        {
          id: "invoices",
          iconName: "file-text",
          children: [
            { id: "allInvoices", href: "/dashboard/invoices" },
            { id: "approvedInvoices", href: "/dashboard/invoices/approved" },
            { id: "rejectedInvoices", href: "/dashboard/invoices/rejected" },
          ],
        },
        {
          id: "disputes",
          iconName: "alert-circle",
          children: [
            { id: "allDisputes", href: "/dashboard/disputes" },
            { id: "createDispute", href: "/dashboard/disputes/create" },
            { id: "inProgressDisputes", href: "/dashboard/disputes/in-progress" },
          ],
        },
        {
          id: "auditReports",
          iconName: "file-text",
          children: [
            { id: "allAuditReports", href: "/dashboard/audit-reports" },
            { id: "submitAuditReport", href: "/dashboard/audit-reports/submit" },
          ],
        },
      ],
    },
    {
      id: "consultation",
      titleKey: "consultation",
      items: [
        { id: "admissions", iconName: "plus-square", href: "/dashboard/admissions" },
        {
          id: "acts",
          iconName: "stethoscope",
          children: [
            { id: "hospitalActs", href: "/dashboard/acts/by-hospital" },
            { id: "consultActs", href: "/dashboard/acts/standard-codes" },
            { id: "consultDiagnostics", href: "/dashboard/acts/standard-diagnostics" },
          ],
        },
      ],
    },
    {
      id: "authorizations",
      titleKey: "authorizations",
      items: [
        { id: "requestsToValidate", iconName: "clipboard", href: "/dashboard/requests" },
        {
          id: "statistics",
          iconName: "bar-chart",
          children: [{ id: "globalStatistics", href: "/dashboard/statistics" }],
        },
      ],
    },
    {
      id: "communication",
      titleKey: "communication",
      items: [{ id: "messaging", iconName: "message-circle", href: "/dashboard/messaging" }],
    },
  ],
};

/** Navigation compte entreprise (CEEC) */
export const enterpriseSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "enterprise-main",
      items: [
        {
          id: "enterpriseDashboard",
          iconName: "home",
          href: "/dashboard/enterprise",
        },
        {
          id: "invoices",
          iconName: "file-text",
          children: [
            { id: "allInvoices", href: "/dashboard/invoices" },
            { id: "approvedInvoicesToPay", href: "/dashboard/invoices/approved" },
          ],
        },
        {
          id: "employeeManagement",
          iconName: "user-cog",
          children: [
            { id: "employeeList", href: "/dashboard/employees" },
            { id: "beneficiaryList", href: "/dashboard/beneficiaries" },
          ],
        },
        { id: "admissions", iconName: "plus-square", href: "/dashboard/admissions" },
      ],
    },
    {
      id: "communication",
      titleKey: "communication",
      items: [{ id: "messaging", iconName: "message-circle", href: "/dashboard/messaging" }],
    },
    {
      id: "configuration",
      titleKey: "configuration",
      items: [
        {
          id: "settings",
          iconName: "settings",
          children: [
            { id: "userProfile", href: "/dashboard/settings/user" },
            { id: "companyProfile", href: "/dashboard/settings/company" },
          ],
        },
      ],
    },
  ],
};

/** Navigation compte prestataire de soins (hôpital) */
export const hospitalSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "hospital-main",
      items: [
        {
          id: "hospitalDashboard",
          iconName: "home",
          href: "/dashboard/hospital",
        },
      ],
    },
    {
      id: "operations",
      titleKey: "operations",
      items: [
        {
          id: "invoices",
          iconName: "file-text",
          children: [
            { id: "viewInvoices", href: "/dashboard/invoices" },
            { id: "submitInvoice", href: "/dashboard/invoices/submit" },
          ],
        },
        {
          id: "admissions",
          iconName: "plus-square",
          children: [
            { id: "newAdmission", href: "/dashboard/admissions/create" },
            { id: "allAdmissions", href: "/dashboard/admissions" },
          ],
        },
        {
          id: "requests",
          iconName: "clipboard",
          href: "/dashboard/requests",
        },
        {
          id: "disputes",
          iconName: "message-alert",
          children: [
            { id: "allDisputes", href: "/dashboard/disputes" },
            { id: "createDispute", href: "/dashboard/disputes/create" },
            { id: "inProgressDisputes", href: "/dashboard/disputes/in-progress" },
          ],
        },
      ],
    },
    {
      id: "communication",
      titleKey: "communication",
      items: [{ id: "messaging", iconName: "message-circle", href: "/dashboard/messaging" }],
    },
    {
      id: "configuration",
      titleKey: "configuration",
      items: [
        {
          id: "settings",
          iconName: "settings",
          children: [
            { id: "userProfile", href: "/dashboard/settings/user" },
            { id: "providerProfile", href: "/dashboard/settings/hospital" },
            { id: "medicalActs", href: "/dashboard/acts/by-hospital" },
            { id: "pharmacyCatalog", href: "/dashboard/pharmacy/products" },
            { id: "categoryValidation", href: "/dashboard/acts/category-validation" },
            { id: "diagnostics", href: "/dashboard/acts/hospital-diagnoses" },
          ],
        },
      ],
    },
  ],
};

export function getSidebarConfig(accountType: AccountType | null): MainSidebarConfig {
  if (accountType === "enterprise") return enterpriseSidebarConfig;
  if (accountType === "hospital") return hospitalSidebarConfig;
  if (accountType === "auditor") return auditorSidebarConfig;
  if (accountType === "admin" && isSuperAdminUser()) {
    return {
      sections: [...mainSidebarConfig.sections, superAdminSystemSection],
    };
  }
  return mainSidebarConfig;
}
